import PropTypes from 'prop-types'
import React from 'react'
import css from './search-bar.css'
import SearchDropDown from './search-drop-down'
import ChannelBox from './channel-box'
import { some, take, shuffle, filter } from 'lodash'
import { getScrollTop } from 'shared/util/scrolling'
import { debounce, autobind } from 'core-decorators'
import { search, channelSearch, channelSocialSearch } from 'shared/api'
import trendingSearchTerms from './trending-search-terms'
import { isAndroid, isiOS } from 'mobile/shared/environment'
import { googleAnalyticsEvent } from 'shared/util/analytics'
import TapLink from 'shared/components/tap-link'
import InputPlaceholderEducationAnimation from './input-placeholder-education'
import { getSessionValue } from '../../../shared/redux/init-store'
import SearchButton from './search-button'

const SEARCH_HEIGHT = 44
const { bool, number, func } = PropTypes
const searchResultsCache = { '': [] }
const channelSearchResultsCache = { '': [] }
const LARGE_PHONE_SIZE = 628 // iPhone 6 and up
const gaEventSearchStart = () => googleAnalyticsEvent({ action: 'Focus', category: 'Search Bar', label: 'Start' })
const gaEventSearchSubmit = () => googleAnalyticsEvent({ action: 'Search', category: 'Search Bar', label: 'Query' })

export default class SearchBar extends React.Component {
    static propTypes = {
        isOffScreen: bool,
        needsFixed: bool,
        scrollTop: number,
        isSticker: bool,
        onFocus: func,
        onBlur: func,
    }
    trendingDropDownItems = trendingSearchTerms.map(name => ({
        name,
        link: `/search/${encodeURIComponent(name)}`,
        type: 'trending',
    }))
    state = {
        currentInput: '',
        currentSelectedChannel: '',
        currentSelectedChannelImage: '',
        hideCancel: true,
        top: 0,
        searchType: '',
        inputText: '',
        needsAbsolute: false,
        dropDownItems: [],
        currentCursorPosition: null,
        placeholderText: '',
        showAnimationPlaceholder: true,
        backspaceOnChannelBox: false,
        isInputFocused: false,
        searchBarMaxHeight: 0,
        suppressDropdown: true,
        focusedIndex: -1,
        searchMade: false,
    }

    componentWillMount() {
        const { searchTerm, isDesktop, pageType } = this.props
        const isChannelSearch = searchTerm && searchTerm.charAt(0) === '@'
        if (isChannelSearch) {
            this.channelSearchForUserTerm(true)
            this.setState({
                currentCursorPosition: this.getSearchTermWithoutChannelName(this.props.searchTerm).length,
            })
        }

        if (searchTerm && !isDesktop && pageType !== 'EXPLORE') {
            this.setState({
                inputText: isChannelSearch
                    ? this.getSearchTermWithoutChannelName(this.props.searchTerm)
                    : this.props.searchTerm || '',
            })
        }
    }

    componentDidMount() {
        const { isDesktop, searchTerm, pageType } = this.props

        // autofocus searchbar on desktop (mobile would trigger keyboard)
        if (searchTerm && pageType !== 'EXPLORE') {
            this.setState({ showAnimationPlaceholder: false })
        }

        if (searchTerm && isDesktop && pageType !== 'EXPLORE') {
            this.setState({
                inputText:
                    this.props.searchTerm[0] === '@'
                        ? this.getSearchTermWithoutChannelName(this.props.searchTerm)
                        : this.props.searchTerm || '',
            })
        }

        if (isDesktop) {
            this.input.focus()
        } else {
            this.setState({
                suppressDropdown: true,
            })
        }

        this.checkCancelButton = this.checkCancelButton.bind(this)
        // when using the browser history, safari seems to clear the input field
        // all by itself.
        this.checkCancelButtonOnDelay = () => {
            this.checkCancelButtonTimeout = setTimeout(this.checkCancelButton, 50)
        }
        // the event that fires on browser history is popstate, check the cancel
        // after a delay
        window.addEventListener('popstate', this.checkCancelButtonOnDelay, false)

        this.setMaxItems(this.props.appHeight)
        this.checkCancelButton()
    }

    shouldComponentUpdate(nextProps, nextState) {
        // we'll change the css if needsFixed changes
        if (nextProps.needsFixed !== this.props.needsFixed) {
            return true
        }
        // we'll only update when scrollTop changes if needsAbsolute is true
        if (this.state.needsAbsolute && nextProps.scrollTop !== this.props.scrollTop) {
            return true
        }
        // always update on the state changes
        if (some(['hideCancel', 'top', 'needsAbsolute'], prop => nextState[prop] !== this.state.prop)) {
            return true
        }
        return false
    }
    componentWillReceiveProps(nextProps) {
        // if we go offScreen to reset the search bar
        if (!this.props.isOffScreen && nextProps.isOffScreen) {
            this.doBlur()
        }

        // on Android, we can't just blur or setSearchBarHeight when appHeight changes,
        // because it changes when the keyboard appears
        if (isAndroid() && nextProps.appHeight > this.props.appHeight) {
            setTimeout(() => {
                if (this.input === document.activeElement) {
                    this.doBlur()
                }
            }, 100)
        } else if (isiOS() && nextProps.appHeight !== this.props.appHeight) {
            // on iOS, the keyboard doesn't change the appHeight, we can remeasure safely
            this.setSearchBarHeight(nextProps.appHeight)
        }
    }
    componentDidUpdate(prevProps) {
        if (this.state.needsAbsolute) {
            // if we're in needsAbsolute mode, set scrollTop if it changes
            if (prevProps.scrollTop !== this.props.scrollTop) {
                // getScrollTop is layout thrashing, do it minimally
                this.setState({ top: getScrollTop() })
            }
        }
    }

    componentWillUnmount() {
        // playing it safe
        clearTimeout(this.checkCancelButtonTimeout)
        this.input.removeEventListener('keyup', this.checkCancelButton, false)
        window.removeEventListener('popstate', this.checkCancelButtonOnDelay, false)
    }
    setSearchBarHeight(appHeight) {
        const browserAdjustment = isAndroid() ? 0 : appHeight < LARGE_PHONE_SIZE ? 0 : 50 // if we're on iPhone 6 and up

        this.setState({
            searchBarMaxHeight: appHeight / 2 - SEARCH_HEIGHT + browserAdjustment,
        })
    }
    setMaxItems(appHeight) {
        if (appHeight < LARGE_PHONE_SIZE) {
            this.maxItems = 3
            this.maxChannels = 1
            this.maxSocialChannels = 4
        } else {
            this.maxItems = isAndroid() ? 4 : 5
            this.maxChannels = 2
            this.maxSocialChannels = isAndroid() ? 7 : 6
        }
    }

    async search() {
        this.setState({ searchMade: true })

        gaEventSearchSubmit()

        // When we use one store, just use the redux value of searchType
        // which will be in session storage via redux-sessionstorage.
        // With two stores, the value search-bar receives will always be initalized correctly from sessionStorage,
        // but if it's toggled the separate store doesn't know and we have to read it here
        const isStickerMode = getSessionValue('searchType') === 'sticker'

        // this.props.isSticker comes is provided by connect
        const query = this.props.isSticker || isStickerMode ? `${this.input.value} stickers` : this.input.value

        if (this.state.currentSelectedChannel) {
            location.href = `/search/?q=${this.state.currentSelectedChannel}+${query}`
        } else {
            location.href = `/search/?q=${query}`
        }
    }

    checkCancelButton() {
        if (!this.input) {
            return // saftey precaution for the popstate that fires on a delay
        }

        this.setState({
            hideCancel: !this.state.inputText,
        })
    }
    getSearchTerm() {
        if (!this.input) {
            return ''
        }
        return (this.state.inputText || '').trim().toLowerCase()
    }

    checkCursorPosition() {
        var myElement = document.getElementById('search-box')
        var startPosition = myElement.selectionStart
        this.setState({ currentCursorPosition: startPosition })
    }

    getSearchTermWithoutChannelName(searchTerm) {
        const searchQuery = searchTerm.split(' ').splice(1, searchTerm.split(' ').length - 1)

        if (searchQuery.length === 1) {
            return searchQuery[0]
        } else if (searchQuery.length > 0) {
            return searchQuery.join(' ')
        } else {
            return ''
        }
    }

    getChannelName(searchTerm) {
        return searchTerm.split(' ')[0].substring(1)
    }

    checkForChannelSearchExactMatch(isSearchPage) {
        const { isDesktop } = this.props
        // get the search term from props if it is a url search
        const searchTerm = isSearchPage ? this.props.searchTerm : this.state.inputText
        const channelNameWithoutAtSign = this.getChannelName(searchTerm)
        const searchWithoutChannelName = this.getSearchTermWithoutChannelName(searchTerm)

        Promise.resolve(channelSocialSearch(channelNameWithoutAtSign)).then(channels => {
            // take MAX_NUMBER_OF_CHANNELS and map them to the dropdown format
            // handle for case of no channels existing on that search and then match the search for an exact match on channel
            if (channels && channels[0].slug === channelNameWithoutAtSign.toLowerCase()) {
                this.selectAChannel(channels[0].slug, channels[0].icon, true)
                this.setState({
                    inputText: searchWithoutChannelName,
                    placeholderText: searchWithoutChannelName
                        ? ''
                        : isDesktop
                            ? 'Add a tag to search this channel’s GIFs and Stickers'
                            : 'Search channel',
                    showAnimationPlaceholder: false,
                    currentCursorPosition: this.getSearchTermWithoutChannelName(searchTerm).length,
                })
            } else {
                this.setState({
                    inputText: `${searchTerm} `,
                    placeholderText: '',
                    showAnimationPlaceholder: false,
                })
            }
            // store the result so we can display immediately next time
        })
    }

    getDropDownItems(searchTerm, searchType) {
        // check the cache if the cache isnt avail make the request
        if (searchType === 'regularSearch') {
            if (searchResultsCache[searchTerm]) {
                return searchResultsCache[searchTerm]
            } else {
                // request results
                return Promise.all([search(searchTerm), channelSearch(searchTerm)]).then(([tags, channels]) => {
                    // take MAX_NUMBER_OF_CHANNELS and map them to the dropdown format
                    const channelResults = take(channels, this.maxChannels).map(({ name, icon: img, id }) => ({
                        name,
                        img,
                        link: `/channels/id/${id}`,
                        type: 'channels',
                    }))

                    // based on channelResults, choose a number of tags, and map them to dropdown format
                    const tagResults = take(tags, this.maxItems - channelResults.length).map(
                        ({ name, name_encoded }) => ({
                            name: name.toLowerCase(),
                            link: `/search/${name_encoded}`,
                            type: 'tags',
                        }),
                    )

                    // store the result so we can display immediately next time
                    searchResultsCache[searchTerm] = [...tagResults, ...channelResults]
                    return [...tagResults, ...channelResults]
                })
            }
        } else if (searchType === 'socialSearch') {
            if (channelSearchResultsCache[searchTerm]) {
                return channelSearchResultsCache[searchTerm]
            } else {
                // request results
                return Promise.resolve(channelSocialSearch(this.getChannelName(searchTerm))).then(channels => {
                    // take MAX_NUMBER_OF_CHANNELS and map them to the dropdown format
                    const channelResults = take(channels, this.maxSocialChannels).map(
                        ({ name, slug, icon: img, id }) => ({
                            name,
                            slug,
                            img,
                            link: `/channels/id/${id}`,
                            type: 'channels',
                        }),
                    )

                    channelSearchResultsCache[searchTerm] = [...channelResults]
                    return channelResults
                })
            }
        }
    }

    async setDropDownItems(searchTerm, searchType) {
        // check to see
        const dropDownItems = await this.getDropDownItems(searchTerm, searchType)
        const { isInputFocused } = this.state
        if (isInputFocused && searchTerm === this.lastSearchedTerm) {
            this.setState({
                dropDownItems,
            })
        }
    }

    @debounce(250)
    channelSearchForUserTerm(isSearchPage) {
        const searchTerm = this.getSearchTerm()
        this.lastSearchedTerm = searchTerm

        if (isSearchPage) {
            this.checkForChannelSearchExactMatch(isSearchPage)
        } else {
            this.setDropDownItems(searchTerm, 'socialSearch')
        }
    }

    @debounce(250)
    searchForUserTerm() {
        const searchTerm = this.getSearchTerm()
        this.lastSearchedTerm = searchTerm
        this.setDropDownItems(searchTerm, 'regularSearch')
    }

    @autobind
    selectAChannel(channel, img, isSearchPage) {
        const { isDesktop } = this.props
        this.setState({
            currentSelectedChannel: `@${channel.trim().toLowerCase()}`,
            currentSelectedChannelImage: img,
            suppressDropdown: true,
            placeholderText: isDesktop ? 'Add a tag to search this channel’s GIFs and Stickers' : 'Search channel',
        })

        // this handles the case for mobile url search
        if (isSearchPage) {
            if (isDesktop) {
                setTimeout(() => {
                    this.input.focus()
                    this.setState({ isInputFocused: true })
                }, 500)
            }
        } else {
            this.setState({ inputText: '' })
            setTimeout(() => {
                this.input.focus()
                this.setState({ isInputFocused: true, currentCursorPosition: 0 })
            }, 500)
        }
    }

    determineSearchType() {
        const searchTerm = this.getSearchTerm()
        if (searchTerm[0] === '@') {
            this.setState({ searchType: 'channel' })
            this.setMaxItems(this.props.appHeight, true)
            this.channelSearchForUserTerm()
        } else {
            this.setState({ searchType: '' })
            this.setMaxItems(this.props.appHeight, false)
            this.searchForUserTerm()
        }
    }

    doBlur() {
        if (this.input) {
            // just in case we didn't have focus somehow
            this.input.blur()
        }
        this.onBlur()
    }

    onBlur() {
        this.input.removeEventListener('keyup', this.checkCancelButton, false)
        this.checkCancelButton() // perhaps unneccessary

        this.setState({
            isInputFocused: false,
            focusedIndex: -1,
        })

        // we did nothing on focus on android, do nothing now
        if (this.props.isFixed) {
            return
        }

        // iOS and keyboard, go back to sticky with top as 0
        this.setState({
            needsAbsolute: false,
            top: 0,
        })

        // func for blur, useful if dontPosition is used
        const { onBlur = () => {} } = this.props
        onBlur()
    }
    onFocus() {
        const { isDesktop } = this.props
        this.setSearchBarHeight(this.props.appHeight)

        // behavior for the cancel button
        this.input.addEventListener('keyup', this.checkCancelButton, false)

        // default trending search terms
        if (!this.state.inputText && !isDesktop && !this.state.currentSelectedChannel) {
            this.setState({
                suppressDropdown: false,
                dropDownItems: take(shuffle(this.trendingDropDownItems), this.maxItems),
            })
        } else {
            this.determineSearchType()
        }

        // sometimes showing the dropdown right away would cause the input to blur (chrome desktop, and android)
        setTimeout(
            () =>
                this.setState({
                    isInputFocused: this.input === document.activeElement,
                }),
            100,
        )
        gaEventSearchStart()

        // android works okay with just fixed
        // desktop is fixed because it's within a container that is minimized on scroll
        if (this.props.isFixed) {
            return
        }

        // iOS and keyboard, we go absolute
        this.setState({
            needsAbsolute: true,
            top: getScrollTop(),
        })

        // func for focus, useful if dontPosition is used
        const { onFocus = () => {} } = this.props
        onFocus()
    }

    handleArrowUp() {
        const { focusedIndex, dropDownItems } = this.state
        if (dropDownItems.length > 0 && focusedIndex > -1) {
            this.setState({ focusedIndex: focusedIndex - 1 })
        }
    }

    handleArrowDown() {
        const { focusedIndex, dropDownItems, searchType } = this.state

        if (dropDownItems.length > 0 && searchType !== 'channel') {
            if (
                focusedIndex <
                filter(dropDownItems, item => {
                    return item.type === 'tags'
                }).length -
                    1
            ) {
                this.setState({ focusedIndex: focusedIndex + 1 })
            }
        } else if (focusedIndex < dropDownItems.length - 1) {
            this.setState({ focusedIndex: focusedIndex + 1 })
        }
    }

    onKeyUpInput() {
        this.checkCancelButton()
        this.checkCursorPosition()
    }

    handleShowPlaceholder() {
        const { isDesktop } = this.props

        if (this.state.inputText) {
            this.setState({ placeholderText: '', showAnimationPlaceholder: false })
        } else if (this.state.currentSelectedChannel) {
            this.setState({
                placeholderText: isDesktop ? 'Add a tag to search this channel’s GIFs and Stickers' : 'Search channel',
                showAnimationPlaceholder: false,
            })
        } else {
            this.setState({ placeholderText: '', showAnimationPlaceholder: true })
        }
    }

    handleInputChange(e) {
        this.setState({ inputText: e.target.value }, () => {
            this.handleShowPlaceholder()
            this.determineSearchType()
        })

        this.checkCursorPosition()
        if (this.state.currentSelectedChannel) {
            this.setState({ inputText: e.target.value })
        }

        if (this.state.dropDownItems.length === 0) {
            this.setState({ focusedIndex: -1 })
        }
    }

    onCancel() {
        this.setState({
            hideCancel: true,
            inputText: '',
            showAnimationPlaceholder: true,
            currentSelectedChannel: '',
            dropDownItems: [],
        })

        // need this because the keyboard kills the focus on the input when we hit the cancel button
        setTimeout(() => {
            this.input.focus()
            this.setState({ isInputFocused: true })
        }, 101)
    }

    onKeydown(e) {
        const { isDesktop } = this.props
        const isaTypingChannelSearch = this.state.inputText[0] === '@' && this.state.inputText.split(' ').length === 1
        // allow the dropdown to be displayed now that the user has shown intent to search
        if (!this.state.currentSelectedChannel) {
            this.setState({
                suppressDropdown: false,
            })
        }

        switch (e.keyCode) {
            // BACKSPACE
            case 8:
                if (this.state.currentSelectedChannel && this.state.currentCursorPosition === 0 && !isDesktop) {
                    this.setState({
                        currentSelectedChannel: '',
                        backspaceOnChannelBox: false,
                        placeholderText: '',
                    })

                    if (!this.state.inputText) {
                        this.setState({ showAnimationPlaceholder: true })
                    }
                } else if (this.state.currentSelectedChannel && this.state.currentCursorPosition === 0) {
                    this.setState({ backspaceOnChannelBox: true })
                }
                // if the cursor position is at the beginning of the input and they have backspaced once
                // remove the channel from currentChannel
                if (this.state.backspaceOnChannelBox === true && this.state.currentCursorPosition === 0) {
                    this.setState({
                        currentSelectedChannel: '',
                        backspaceOnChannelBox: false,
                        placeholderText: '',
                    })

                    if (!this.state.inputText) {
                        this.setState({ showAnimationPlaceholder: true })
                    }
                    break
                    // if they backspace once and the click somewhere else and backspace again
                    // we get rid of the initial backspaced state
                } else if (this.state.backspaceOnChannelBox === true) {
                    this.setState({ backspaceOnChannelBox: false })
                }
                break
            // SPACEBAR
            case 32:
                if (isaTypingChannelSearch) {
                    this.checkForChannelSearchExactMatch()
                } else {
                    this.setState({ focusedIndex: -1 })
                }
                break
            // ARROW UP
            case 38:
                isDesktop && this.handleArrowUp()
                e.preventDefault()
                break
            // ARROW DOWN
            case 40:
                isDesktop && this.handleArrowDown()
                e.preventDefault()
                break
            // ENTER
            case 13:
                if (
                    this.state.dropDownItems.length > 0 &&
                    this.state.suppressDropdown === false &&
                    this.state.focusedIndex > -1 &&
                    isDesktop
                ) {
                    const dropDownItem = this.state.dropDownItems[this.state.focusedIndex]
                    this.setState({ inputText: dropDownItem.name, suppressDropdown: true, focusedIndex: -1 })
                    if (dropDownItem.type === 'channels') {
                        this.selectAChannel(dropDownItem.slug, dropDownItem.img)
                    }
                } else {
                    this.setState({ suppressDropdown: true, searchMade: true })
                    this.search()
                }
                break

            default:
                break
        }
    }

    render() {
        const { needsFixed, isDesktop, dontPosition, style: styleProp = {} } = this.props
        const {
            hideCancel,
            needsAbsolute,
            top,
            currentSelectedChannelImage,
            dropDownItems,
            isInputFocused,
            suppressDropdown,
            inputText,
            backspaceOnChannelBox,
            searchMade,
            searchType,
            placeholderText,
            showAnimationPlaceholder,
            currentSelectedChannel,
            focusedIndex,
        } = this.state

        const device = isDesktop ? `desktop` : `mobile`
        // on Android put the search bar in fixed even if we haven't scrolled,
        // this makes more room for the dropdown
        const fixedForActiveInput = isAndroid() && this.input === document.activeElement
        // We'll use fixed for android and
        const className =
            needsFixed || fixedForActiveInput
                ? css.containerFixed
                : needsAbsolute // use absolute on iOS when the keyboard is present
                    ? css.containerAbsolute
                    : css[`${device}Container`]

        let inputStyle = {}
        if (showAnimationPlaceholder) {
            inputStyle = { backgroundColor: 'transparent' }
        } else if (currentSelectedChannel && !isDesktop) {
            inputStyle = { paddingLeft: 45 }
        } else if (currentSelectedChannel) {
            inputStyle = { paddingLeft: 12 }
        }
        const style = { top, ...styleProp }
        if (dontPosition) {
            style.position = 'static'
        }
        return (
            <div style={style} className={className}>
                {!isDesktop && (
                    <div
                        className={isInputFocused ? css.blockPage : null}
                        onTouchStart={() => this.input.blur()}
                        onTouchEnd={e => e.preventDefault()}
                        onClick={e => e.preventDefault()}
                    />
                )}
                <div className={css[`${device}SearchbarInputContainer`]}>
                    {currentSelectedChannel ? (
                        <ChannelBox
                            backspaceOnChannelBox={backspaceOnChannelBox}
                            device={device}
                            currentSelectedChannelImage={currentSelectedChannelImage}
                            currentSelectedChannel={currentSelectedChannel}
                        />
                    ) : null}
                    <form
                        className={css.searchForm}
                        ref={c => (this.form = c)}
                        action=""
                        method=""
                        onSubmit={e => {
                            e.preventDefault()
                        }}
                    >
                        <TapLink
                            className={hideCancel ? css.searchCancelHidden : css.searchCancel}
                            onTap={() => this.onCancel()}
                        >
                            {hideCancel || isDesktop ? null : <img src="/static/img/mobile/x.png" />}
                        </TapLink>

                        <p
                            onClick={() => this.input.focus()}
                            className={
                                currentSelectedChannel
                                    ? css[`${device}ChannelPlaceholder`]
                                    : css[`${device}RegularPlaceholder`]
                            }
                        >
                            {placeholderText}
                        </p>
                        <InputPlaceholderEducationAnimation
                            device={device}
                            showAnimationPlaceholder={showAnimationPlaceholder}
                            input={this.input}
                            css={css}
                            rollInPlaceholderText={'@username + tag to search within a verified channel'}
                            placeholderText={'Search all the GIFs and Stickers'}
                            mobilePlaceholderText={'Search all the GIFs and Stickers'}
                            mobileRollInPlaceholderText={'@username to search channels'}
                        />
                        <input
                            className={`${css.searchInput} ${
                                searchType === 'channel' &&
                                !currentSelectedChannel &&
                                dropDownItems &&
                                dropDownItems.length > 0
                                    ? css.channelInput
                                    : null
                            } ${currentSelectedChannel ? css.channelSearch : null}`}
                            style={inputStyle}
                            ref={c => (this.input = c)}
                            id="search-box"
                            type="text"
                            value={inputText}
                            onFocus={() => this.onFocus()}
                            onKeyDown={e => this.onKeydown(e)}
                            onBlur={() => this.doBlur()}
                            onKeyUp={() => this.onKeyUpInput()}
                            autoCapitalize="off"
                            onChange={e => this.handleInputChange(e)}
                            autoCorrect="off"
                            autoComplete="off"
                        />
                        <button type="submit" className={css.hidden} />
                    </form>
                </div>
                <TapLink className={css[`${device}SearchbarButton`]} onTap={() => this.search()}>
                    <SearchButton isSearching={searchMade} device={device} />
                </TapLink>
                <SearchDropDown
                    items={isInputFocused && !suppressDropdown ? dropDownItems : []}
                    searchType={searchType}
                    onChannelSelected={this.selectAChannel}
                    device={device}
                    focusedIndex={focusedIndex}
                />
            </div>
        )
    }
}
